import type {ReactNode, ComponentPropsWithoutRef, ComponentProps} from 'react';
import React, {createElement} from 'react';

import {hashedClasses, withCssModule} from '@pb/kit-util-with-css-module';

import {COLOR_VARIANT_CLASSES, VARIANT_CLASSES} from './constants';
import {ButtonIcon} from './button-icon.component';

import styles from './button.component.module.styl';

const classNames = withCssModule(styles);
const classes = hashedClasses(styles);
const noop = () => undefined;

type NativeButtonProps = ComponentProps<'button'>;

export interface ButtonProps extends NativeButtonProps {
    /**
     * Root element type. Use `"a"` together with `href` to render a link that
     * is visually indistinguishable from a button. Defaults to `"button"`.
     */
    as?: 'button' | 'a',

    /**
     * When `true`, renders a three-dot loading indicator over the button, sets
     * `aria-disabled` and suppresses click events. Override `aria-label` to
     * provide a screen-reader-friendly "Loading…" description for the specific
     * action. Defaults to `false`.
     */
    busy?: boolean,

    /**
     * When `true`, renders the button with equal width and height as a circle —
     * intended for icon-only buttons. Defaults to `false`.
     */
    circled?: boolean,

    /**
     * When `true`, applies an active/selected appearance. Useful for toggle
     * buttons that represent a currently chosen option. Defaults to `false`.
     */
    current?: boolean,

    /**
     * URL the element navigates to when `as="a"`. Has no effect when rendering
     * as a `<button>`. Temporary prop until the legacy Flow layer is removed.
     */
    href?: ComponentPropsWithoutRef<'a'>['href'],

    /**
     * Link target attribute when `as="a"` (e.g. `"_blank"`). Has no effect when
     * rendering as a `<button>`. Temporary prop until the legacy Flow layer is removed.
     */
    target?: ComponentPropsWithoutRef<'a'>['target'],

    /**
     * Visual style preset controlling shape, fill, and emphasis level:
     * - `"Type.PRIMARY"` — high-emphasis filled button (default).
     * - `"Type.SECONDARY"` — medium-emphasis outlined button.
     * - `"Type.TERTIARY"` — low-emphasis ghost/text button.
     * - `"Type.PAGINATION"` — compact style for paginator controls.
     * - `"Type.BACK"` — back-navigation arrow style.
     */
    variant?: 'Type.PRIMARY' | 'Type.SECONDARY' | 'Type.TERTIARY' | 'Type.PAGINATION' | 'Type.BACK',

    /**
     * Color theme applied on top of the `variant` style:
     * - `"DEFAULT"` — standard brand color (default).
     * - `"ERROR"` — destructive red, for delete/remove actions.
     * - `"ACCENT"` — accent color for promoted/featured actions.
     */
    colorVariant?: 'ERROR' | 'ACCENT' | 'DEFAULT',

    /**
     * Size preset controlling padding and font size:
     * - `"normal"` — standard size (default).
     * - `"small"` — compact size for dense UI areas.
     */
    size?: 'small' | 'normal',

    /**
     * Applies legacy dark-mode overrides. Has no effect on `"Type.PAGINATION"`
     * or `"Type.BACK"` variants.
     *
     * @deprecated It will be removed in 6 months 03-03-2026. Use default appearance
     */
    darkMode?: boolean,

    /**
     * Forwarded ref to the underlying `<button>` (or `<a>`) element.
     */
    ref?: React.Ref<HTMLButtonElement>,

    /**
     * Icon rendered to the left of the button label. Accepts any React node —
     * typically an `<Icon>` component.
     */
    startIcon?: ReactNode,

    /**
     * Icon rendered to the right of the button label. Accepts any React node —
     * typically an `<Icon>` component.
     */
    endIcon?: ReactNode,
}


/**
 * Primary action button with multiple visual variants, busy state, and optional
 * leading/trailing icons.
 *
 * Renders as a `<button>` by default; pass `as="a"` with an `href` to render an
 * accessible link styled as a button. Five `variant` styles are available
 * (`Type.PRIMARY`, `Type.SECONDARY`, `Type.TERTIARY`, `Type.PAGINATION`,
 * `Type.BACK`) combined with three `colorVariant` options (`DEFAULT`, `ERROR`,
 * `ACCENT`). Use `startIcon`/`endIcon` to place icons flanking the label text.
 *
 * When `busy` is `true` the button is visually replaced with a three-dot loading
 * indicator, click events are suppressed (`noop`), and `aria-label` defaults to
 * `"Loading"` — override with an explicit `aria-label` when the context requires
 * a more descriptive announcement.
 *
 * ## Accessibility
 * When Button is busy — add the appropriate `aria-label` for the Button.
 * This component implements the WAI-ARIA authoring practices for a button role.
 * For more information, see [WAI-ARIA Authoring Practices](https://www.w3.org/WAI/ARIA/apg/patterns/button/).
 *
 * @see [Storybook]{@link https://storybook.dev.pitchbookdata.com/?path=/docs/components-button--default-button}
 */
export function Button(props: ButtonProps) {
    const {
        as = 'button',
        className,
        darkMode,
        current,
        circled,
        size = 'normal',
        busy,
        startIcon,
        endIcon,
        type = as === 'button' ? 'button' : undefined,
        variant = 'Type.PRIMARY',
        colorVariant = 'DEFAULT',
        disabled,
        ref,
        ...restProps
    } = props;

    const elementClassName = classNames(className, 'button',
        VARIANT_CLASSES[variant],
        COLOR_VARIANT_CLASSES[colorVariant],
        {
            'button_small': size === 'small',
            'button_busy': busy,
            'button_current': current,
            'button_circled': circled,
            'button_dark-mode': darkMode && !(variant === 'Type.PAGINATION' || variant === 'Type.BACK'),
        },
    );

    return createElement(
        as,
        {
            'aria-disabled': busy || disabled,
            ref,
            disabled,
            ...restProps,
            type,
            'onClick': busy ? noop : restProps.onClick,
            ...(busy ? {'aria-label': restProps['aria-label'] || 'Loading'} : {}),
            'className': elementClassName,
        },
        (
            <div className={classes.button__hole}>
                <ButtonIcon>
                    {startIcon}
                </ButtonIcon>
                <span className={classes.button__caption}>{props.children}</span>
                <ButtonIcon right={true}>
                    {endIcon}
                </ButtonIcon>
            </div>
        ),
        busy ? (
            <div className={classes.button__busy} aria-hidden="true">
                <span className={classes.button__busy_1}/>
                <span className={classes.button__busy_2}/>
                <span className={classes.button__busy_3}/>
            </div>
        ) : null,
    );
}
